"use client";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useStore } from "zustand";
import { useOakConsent } from "@oaknational/oak-consent-client";
import { usePathname } from "next/navigation";

import useAnalytics from "../Analytics/useAnalytics";
import OakError from "../../errors/OakError";
import errorReporter from "../../common-lib/error-reporter";

import {
  createTeacherBrowseAnalyticsStore,
  TeacherBrowseAnalyticsStore,
} from "./TeacherBrowseAnalyticsStore";
import useJourneySlugsContext from "./utils/getJourneySlugsContext";
import { getAccessLevelFromPathname } from "./utils/getAccessLevelFromPathname";

import { ServicePolicyMap } from "@/browser-lib/cookie-consent/ServicePolicyMap";
import useSelectedArea from "@/hooks/useSelectedArea";
import type { TrackFns } from "@/context/Analytics/AnalyticsProvider";

export type TeacherBrowseAnalyticsStoreApi = ReturnType<
  typeof createTeacherBrowseAnalyticsStore
>;
export const TeacherBrowseAnalyticsStoreContext = createContext<
  TeacherBrowseAnalyticsStoreApi | undefined
>(undefined);

export type TeacherBrowseAnalyticsStoreProviderProps = Pick<
  TeacherBrowseAnalyticsStore,
  "programmeState"
> &
  Partial<Pick<TeacherBrowseAnalyticsStore, "accessLevel">> & {
    children: ReactNode;
  };

const reportError = errorReporter("teacher-browse-analytics");

export const TeacherBrowseAnalyticsStoreProvider = ({
  programmeState,
  accessLevel: accessLevelProp,
  children,
}: TeacherBrowseAnalyticsStoreProviderProps) => {
  const { track, getSessionId, posthogDistinctId } = useAnalytics();
  const { subjectSlug, phaseSlug } = useJourneySlugsContext();
  const pathname = usePathname();
  const accessLevel = accessLevelProp ?? getAccessLevelFromPathname(pathname);
  const posthogConsent = useOakConsent().getConsent(ServicePolicyMap.POSTHOG);
  const hasConsent = posthogConsent === "granted";

  const journeyId = useMemo(() => {
    if (!hasConsent) {
      return null;
    }
    const sessionId = getSessionId();

    if (!sessionId) {
      // user consented to cookies but does not have a session id
      reportError(
        new OakError({
          code: "analytics/teacher-browse",
          meta: {
            sessionId,
            message: "Missing session id",
          },
        }),
      );
      return null;
    }
    return `${sessionId}:${phaseSlug}-${subjectSlug}`;
  }, [hasConsent, getSessionId, subjectSlug, phaseSlug]);

  const [store] = useState(() =>
    createTeacherBrowseAnalyticsStore({
      programmeState,
      avo: track,
      journeyId,
      accessLevel,
      posthogDistinctId,
    }),
  );

  useEffect(() => {
    store.setState({
      journeyId,
      posthogDistinctId,
    });
  }, [store, journeyId, posthogDistinctId]);

  return (
    <TeacherBrowseAnalyticsStoreContext.Provider value={store}>
      {children}
    </TeacherBrowseAnalyticsStoreContext.Provider>
  );
};

export const useTeacherBrowseAnalytics = <T,>(
  selector: (store: TeacherBrowseAnalyticsStore) => T,
): T => {
  const activeArea = useSelectedArea();
  const teacherBrowseAnalyticsStoreContext = useContext(
    TeacherBrowseAnalyticsStoreContext,
  );

  // Hooks must run unconditionally, so a store is always needed here. In the
  // pupils area there is no provider, so lazily create an inert one to satisfy
  // useStore without throwing or tracking anything.
  const [fallbackStore] = useState(() =>
    createTeacherBrowseAnalyticsStore({
      programmeState: null,
      avo: new Proxy({} as TrackFns, { get: () => () => undefined }),
      journeyId: null,
      accessLevel: "homepage",
      posthogDistinctId: null,
    }),
  );

  if (activeArea === "TEACHERS" && !teacherBrowseAnalyticsStoreContext) {
    throw new Error(
      `useTeacherBrowseAnalyticsStore must be used within TeacherBrowseAnalyticsStoreProvider`,
    );
  }

  return useStore(
    activeArea === "PUPILS"
      ? fallbackStore
      : (teacherBrowseAnalyticsStoreContext as TeacherBrowseAnalyticsStoreApi),
    selector,
  );
};
